import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  Stack,
  Typography
} from '@mui/material';
import { TUDCheckbox } from '../Form/TUDCheckbox';
import LoadingButton from '@mui/lab/LoadingButton';
import Grid from '@mui/material/Unstable_Grid2';

export interface NewsletterStackProps {
  onNewsletterChange: (newsletters: Record<string, boolean>) => void;
  selectedNewsletters: Record<string, boolean>;
  newsletters: Newsletter[];
  variant?: 'big' | 'small';
}

interface NewsletterSelectorProps extends NewsletterStackProps {
  newsletters: Newsletter[];
  saving?: boolean;
}

const isChecked = (
  newsletter: Newsletter,
  selectedNewsletters: Record<string, boolean>
) => {
  if (newsletter.mailchimp_interest) {
    return selectedNewsletters[newsletter.mailchimp_interest];
  }
  return false;
};

function renderNewsletterCheckbox(
  newsletter: Newsletter,
  selectedNewsletters: Record<string, boolean>,
  handleCheckboxChange: (newsletterId?: string | null) => void
) {
  return (
    <FormControl key={newsletter.id}>
      <FormControlLabel
        control={
          <TUDCheckbox
            checked={isChecked(newsletter, selectedNewsletters)}
            onChange={() => handleCheckboxChange(newsletter.mailchimp_interest)}
          />
        }
        label={
          <Typography variant='h3' style={{ margin: 0 }}>
            {newsletter.name}
          </Typography>
        }
      />
    </FormControl>
  );
}

export const NewsletterStack = ({
  onNewsletterChange,
  selectedNewsletters,
  newsletters,
  variant = 'big'
}: NewsletterStackProps) => {
  const handleCheckboxChange = (newsletterId?: string | null) => {
    if (!newsletterId) {
      return;
    }
    onNewsletterChange({
      ...selectedNewsletters,
      [newsletterId]: !selectedNewsletters[newsletterId]
    });
  };

  if (variant === 'small') {
    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        {newsletters.map((newsletter) =>
          renderNewsletterCheckbox(
            newsletter,
            selectedNewsletters,
            handleCheckboxChange
          )
        )}
      </Stack>
    );
  }

  return (
    <Grid container>
      {newsletters.map((newsletter) => (
        // Weird interaction between Grid, Card and CardActionArea requires display flex here
        <Grid sm={6} key={newsletter.id} display='flex'>
          {/* CardActionArea on the outside makes the whole card clickable */}
          <CardActionArea
            sx={{
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => {
              e.preventDefault();
              handleCheckboxChange(newsletter.mailchimp_interest);
            }}
          >
            <Card
              variant='outlined'
              sx={{
                // FlexGrow makes all cards the same height in a row
                flexGrow: 1,
                borderColor: isChecked(newsletter, selectedNewsletters)
                  ? '#F4A71C'
                  : undefined
              }}
            >
              <CardHeader
                title={renderNewsletterCheckbox(
                  newsletter,
                  selectedNewsletters,
                  handleCheckboxChange
                )}
                subheader={newsletter.frequency}
              />
              <CardContent sx={{ pt: 0 }}>
                <Typography variant='body1'>
                  {newsletter.description}
                </Typography>
              </CardContent>
            </Card>
          </CardActionArea>
        </Grid>
      ))}
    </Grid>
  );
};

const NewsletterSelector = ({
  onNewsletterChange,
  newsletters,
  selectedNewsletters,
  saving = false
}: NewsletterSelectorProps) => {
  return (
    <Stack alignItems='flex-start'>
      <Box>
        <Typography variant='h1'>Stay Informed (optional)</Typography>
        <Typography variant='body1'>
          Manage your newsletter preferences.
        </Typography>
      </Box>
      <NewsletterStack
        newsletters={newsletters}
        onNewsletterChange={onNewsletterChange}
        selectedNewsletters={selectedNewsletters}
      />
      <LoadingButton
        className='btn-continue'
        disabled={!(selectedNewsletters && !saving)}
        variant='outlined'
        disableElevation
        loading={saving}
        color='secondary'
      >
        SAVE PREFERENCES
      </LoadingButton>
    </Stack>
  );
};

export default NewsletterSelector;
